export const API = () => {
  return (
    <div className="space-y-8">
      {/* Hero Section */}
      <div className="relative">
        <div className="absolute inset-0 bg-gradient-to-r from-brand-600 to-purple-600 rounded-3xl blur-3xl opacity-10" />
        <div className="relative bg-white/80 backdrop-blur-sm rounded-3xl p-8 border border-gray-200/50 shadow-xl">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-14 h-14 bg-gradient-to-br from-brand-600 to-purple-700 rounded-2xl flex items-center justify-center shadow-lg shadow-brand-600/50">
              <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
              </svg>
            </div>
            <div>
              <h1 className="text-4xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent">
                TreadAI API
              </h1>
              <p className="text-gray-600 text-lg mt-1">
                AI-powered tire tread measurement for insurance & automotive
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Industry Solutions */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Dealers & Service Centers */}
        <div className="relative group">
          <div className="absolute inset-0 bg-gradient-to-r from-blue-500 to-cyan-600 rounded-3xl blur-xl opacity-20 group-hover:opacity-30 transition-opacity" />
          <div className="relative bg-white/90 backdrop-blur-xl rounded-3xl p-8 border border-gray-200/50 shadow-xl hover:shadow-2xl transition-all">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-16 h-16 bg-gradient-to-br from-blue-600 to-cyan-700 rounded-2xl flex items-center justify-center shadow-lg shadow-blue-600/50">
                <svg className="w-9 h-9 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-900">Dealers & Service Centers</h2>
                <p className="text-sm text-gray-600">Instant tread depth measurements</p>
              </div>
            </div>

            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                  <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1">Pre-Purchase Inspections</h3>
                  <p className="text-sm text-gray-600">
                    Get accurate tread depth readings during trade-ins. Document tire condition with objective AI measurements.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="w-6 h-6 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                  <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1">Fleet Monitoring</h3>
                  <p className="text-sm text-gray-600">
                    Track tread wear across entire vehicle fleets. Schedule proactive replacements before tires become unsafe.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="w-6 h-6 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                  <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1">Service Documentation</h3>
                  <p className="text-sm text-gray-600">
                    Generate professional reports with tread measurements. Build customer trust with data-driven assessments.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Insurance Companies */}
        <div className="relative group">
          <div className="absolute inset-0 bg-gradient-to-r from-emerald-500 to-teal-600 rounded-3xl blur-xl opacity-20 group-hover:opacity-30 transition-opacity" />
          <div className="relative bg-white/90 backdrop-blur-xl rounded-3xl p-8 border border-gray-200/50 shadow-xl hover:shadow-2xl transition-all">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-16 h-16 bg-gradient-to-br from-emerald-600 to-teal-700 rounded-2xl flex items-center justify-center shadow-lg shadow-emerald-600/50">
                <svg className="w-9 h-9 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-900">Insurance Companies</h2>
                <p className="text-sm text-gray-600">Risk assessment & claims verification</p>
              </div>
            </div>

            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 bg-emerald-100 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                  <svg className="w-4 h-4 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1">Risk Assessment</h3>
                  <p className="text-sm text-gray-600">
                    Evaluate vehicle safety with objective tread depth data. Adjust premiums based on actual tire condition.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="w-6 h-6 bg-emerald-100 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                  <svg className="w-4 h-4 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1">Claims Verification</h3>
                  <p className="text-sm text-gray-600">
                    Verify tire-related claims with timestamped measurements. Detect fraud by comparing pre and post-accident conditions.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="w-6 h-6 bg-emerald-100 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                  <svg className="w-4 h-4 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1">Policy Incentives</h3>
                  <p className="text-sm text-gray-600">
                    Offer discounts to policyholders who maintain safe tread levels. Encourage proactive tire maintenance.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* API Endpoints */}
      <div className="relative">
        <div className="absolute inset-0 bg-gradient-to-r from-gray-500 to-gray-700 rounded-3xl blur-3xl opacity-10" />
        <div className="relative bg-white/90 backdrop-blur-xl rounded-3xl p-8 border border-gray-200/50 shadow-2xl">
          <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2">
            <div className="w-8 h-8 bg-gradient-to-br from-gray-700 to-gray-900 rounded-lg flex items-center justify-center">
              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
              </svg>
            </div>
            API Endpoints
          </h2>

          {/* Main Analysis Endpoint */}
          <div className="space-y-4 font-mono text-sm">
            <div className="bg-gradient-to-br from-emerald-50 to-emerald-100/50 rounded-xl p-5 border-2 border-emerald-200">
              <div className="flex items-center gap-3 mb-3">
                <span className="px-3 py-1.5 bg-emerald-500 text-white rounded-lg font-bold text-xs">POST</span>
                <code className="text-gray-900 font-bold text-base">/api/analysis/tire</code>
                <span className="ml-auto px-2 py-1 bg-emerald-100 text-emerald-700 rounded text-xs font-semibold">PRIMARY</span>
              </div>
              <p className="text-gray-700 text-sm mb-4">Analyze a tire image and get tread depth measurement in millimeters.</p>

              <div className="bg-white rounded-lg p-4 border border-emerald-200">
                <p className="text-xs text-gray-500 uppercase font-semibold mb-2">Request</p>
                <code className="text-xs text-gray-700 block mb-4">Content-Type: multipart/form-data<br/>Body: image (file)</code>

                <p className="text-xs text-gray-500 uppercase font-semibold mb-2">Response</p>
                <div className="bg-gray-50 rounded p-3 text-xs text-gray-700 font-mono">
                  <span className="text-emerald-600">tread_mm</span>: number &nbsp;&nbsp;<span className="text-gray-400">// Tread depth in mm (e.g., 6.5)</span><br/>
                  <span className="text-emerald-600">status</span>: string &nbsp;&nbsp;<span className="text-gray-400">// "ok" | "watch" | "replace_soon" | "unsafe"</span><br/>
                  <span className="text-emerald-600">wear_pattern</span>: string &nbsp;&nbsp;<span className="text-gray-400">// "even" | "inner_edge" | "outer_edge" | "center"</span><br/>
                  <span className="text-emerald-600">overall_confidence</span>: number &nbsp;&nbsp;<span className="text-gray-400">// 0-1 confidence score</span><br/>
                  <span className="text-emerald-600">dot_code</span>: string &nbsp;&nbsp;<span className="text-gray-400">// Detected DOT code if visible</span><br/>
                  <span className="text-emerald-600">age_years</span>: number &nbsp;&nbsp;<span className="text-gray-400">// Estimated tire age from DOT</span>
                </div>
              </div>
            </div>

            <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
              <div className="flex items-center gap-3 mb-2">
                <span className="px-2 py-1 bg-emerald-500 text-white rounded font-bold text-xs">POST</span>
                <code className="text-gray-900 font-semibold">/api/analysis/tires</code>
              </div>
              <p className="text-gray-600 text-xs ml-16">Analyze multiple tires in a single image. Returns array of tread measurements for each detected tire.</p>
            </div>

            <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
              <div className="flex items-center gap-3 mb-2">
                <span className="px-2 py-1 bg-emerald-500 text-white rounded font-bold text-xs">POST</span>
                <code className="text-gray-900 font-semibold">/api/vehicle/resolve</code>
              </div>
              <p className="text-gray-600 text-xs ml-16">Look up vehicle information by VIN. Returns year, make, model for record keeping.</p>
            </div>

            <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
              <div className="flex items-center gap-3 mb-2">
                <span className="px-2 py-1 bg-blue-500 text-white rounded font-bold text-xs">GET</span>
                <code className="text-gray-900 font-semibold">/api/health</code>
              </div>
              <p className="text-gray-600 text-xs ml-16">Check API status and ML model availability.</p>
            </div>
          </div>

          <div className="mt-6 p-4 bg-brand-50 rounded-xl border border-brand-200">
            <p className="text-sm text-gray-700">
              <strong>Base URL:</strong> <code className="bg-white px-2 py-1 rounded text-brand-600">https://api.tread-ai.com</code>
            </p>
          </div>
        </div>
      </div>

      {/* Code Example */}
      <div className="relative">
        <div className="absolute inset-0 bg-gradient-to-r from-gray-800 to-gray-900 rounded-3xl blur-3xl opacity-20" />
        <div className="relative bg-gray-900 rounded-3xl p-8 border border-gray-700 shadow-2xl">
          <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
            <svg className="w-5 h-5 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
            </svg>
            Quick Start Example
          </h2>
          <pre className="bg-gray-950 rounded-xl p-4 overflow-x-auto text-sm">
            <code className="text-gray-300">
{`curl -X POST https://api.tread-ai.com/api/analysis/tire \\
  -H "Authorization: Bearer YOUR_API_KEY" \\
  -F "image=@tire_photo.jpg"

# Response:
{
  "tread_mm": 6.5,
  "status": "ok",
  "wear_pattern": "even",
  "overall_confidence": 0.87,
  "dot_code": "DOT 4521",
  "age_years": 2.1
}`}
            </code>
          </pre>
        </div>
      </div>

      {/* CTA Section */}
      <div className="relative group">
        <div className="absolute inset-0 bg-gradient-to-r from-brand-600 to-purple-600 rounded-3xl blur-2xl opacity-20 group-hover:opacity-30 transition-opacity" />
        <div className="relative bg-gradient-to-r from-brand-600 to-purple-700 rounded-3xl p-8 shadow-2xl">
          <div className="text-center text-white">
            <h2 className="text-3xl font-bold mb-3">Get API Access</h2>
            <p className="text-brand-100 text-lg mb-6 max-w-2xl mx-auto">
              Contact us for API keys, volume pricing, and custom integration support.
            </p>
            <div className="flex items-center justify-center gap-4">
              <a
                href="mailto:contact@tread-ai.com?subject=TreadAI API Access"
                className="px-8 py-3 bg-white text-brand-700 rounded-xl font-semibold shadow-lg hover:shadow-xl hover:scale-105 transition-all"
              >
                Request API Access
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
